<template>
	<div
		class="auth-fluid"
		:style="`background: url(${imageUrl}); background-size: cover`"
	>
		<div class="auth-fluid-right text-center">
			<div class="auth-user-testimonial">
				<p class="lead" />
				<h5 class="text-white" />
			</div>
		</div>
		<div class="auth-fluid-form-box">
			<img :src="`${logoUrl}`" class="img-fluid" alt="Logo" />
			<form @submit.prevent="store">
				<grouped-errors ref="groupedErrors" title="Oops" />

				<div v-if="showSponsorField" class="form-row pt-3 mb-2">
					<div class="form-group col-12 required">
						<label for="sponsor">
							Patrocinador
							<small>({{ usernameField.label }})</small>
						</label>
						<input
							id="sponsor"
							v-model="form.sponsor"
							type="text"
							class="form-control"
							name="sponsor"
							:placeholder="`${usernameField.label} do patrocinador`"
						/>
					</div>
				</div>

				<div v-if="people && company" class="form-row">
					<div class="form-group">
						<div
							v-if="people"
							class="radio radio-info form-check-inline ml-md-3 col-12 col-md-auto"
						>
							<input
								id="peopleRadio"
								v-model="form.person_type"
								type="radio"
								value="people"
								name="person_type"
							/>
							<label for="peopleRadio">Pessoa Física</label>
						</div>
						<div
							v-if="company"
							class="radio radio-info form-check-inline col-12 col-md-auto"
						>
							<input
								id="companyRadio"
								v-model="form.person_type"
								type="radio"
								value="company"
								name="person_type"
							/>
							<label for="companyRadio">Pessoa Jurídica</label>
						</div>
					</div>
				</div>

				<div class="form-row">
					<div class="form-group col-12 required">
						<label for="username">
							{{ usernameField.label || 'Username' }}
						</label>
						<input
							id="username"
							v-model="form.username"
							type="text"
							:class="[
								'form-control',
								{
									'is-invalid':
										usernameValidation.available === false,
									'is-valid':
										usernameValidation.available === true,
								},
							]"
							:placeholder="usernameField.placeholder"
							autocomplete="username"
							@input="sanitizeUsername"
							@blur="checkUsernameAvailability"
						/>
						<template v-if="usernameValidation.checking">
							<span class="form-text text-muted">
								<small>Verificando disponibilidade...</small>
							</span>
						</template>
						<template v-else-if="usernameValidation.message">
							<span
								:class="[
									'form-text',
									usernameValidation.available
										? 'text-success'
										: 'text-danger',
								]"
							>
								<small>{{ usernameValidation.message }}</small>
							</span>
						</template>
						<template v-else-if="usernameField.help_text">
							<span class="form-text text-muted">
								{{ usernameField.help_text }}
							</span>
						</template>
						<template v-else>
							<span class="form-text text-muted">
								<small>
									O {{ usernameField.label }} será utilizado
									no link de indicação. Apenas letras,
									números, hífen e underscore são permitidos.
								</small>
							</span>
						</template>
					</div>
				</div>

				<template v-if="form.person_type === 'people'">
					<div v-if="!useSeparateName" class="form-row">
						<div class="form-group col-12 required">
							<label for="full_name">Nome completo</label>
							<input
								id="full_name"
								v-model="form.userable.full_name"
								type="text"
								class="form-control"
								placeholder="Nome completo"
							/>
						</div>
					</div>
					<div v-if="useSeparateName" class="form-row">
						<div class="form-group col-12 required">
							<label for="firstName">Nome</label>
							<input
								id="firstName"
								v-model="form.userable.first_name"
								type="text"
								class="form-control"
								placeholder="Primeiro nome"
							/>
						</div>
					</div>

					<!-- end row -->

					<div v-if="useSeparateName" class="form-row">
						<div class="form-group col-12 required">
							<label for="lastName">Sobrenome</label>
							<input
								id="lastName"
								v-model="form.userable.last_name"
								type="text"
								class="form-control"
								placeholder="Último nome"
							/>
						</div>
					</div>
					<!-- end row -->
				</template>

				<template v-else>
					<div class="form-row required">
						<div
							v-if="showLegalName"
							class="form-group col-md-12 required"
						>
							<label class="col-form-label" for="legal_name">
								Razão social
							</label>
							<input
								id="legal_name"
								v-model="form.userable.legal_name"
								type="text"
								name="legal_name"
								class="form-control"
								placeholder="Empresa ltda"
							/>
						</div>
						<div class="form-group col-md-12 required">
							<label class="col-form-label" for="people_contact">
								Nome do responsável
							</label>
							<input
								id="people_contact"
								v-model="form.userable.people_contact"
								type="text"
								name="people_contact"
								class="form-control"
								placeholder="Nome do responsável"
							/>
						</div>
					</div>
				</template>

				<div class="form-row">
					<div class="form-group col-12 required">
						<label class="col-form-label" for="phone">
							Telefone
						</label>
						<input
							id="phone"
							v-model="form.phone"
							v-mask="'+55 (##) #####-####'"
							type="tel"
							name="phone"
							class="form-control"
							placeholder="+55 (##) #####-####"
						/>
					</div>
				</div>

				<div class="form-row">
					<div class="form-group col-12 required">
						<label for="email">Melhor e-mail</label>
						<input
							id="email"
							v-model="form.email"
							type="email"
							:class="[
								'form-control',
								isEmailValid(form.email, 'parsley-success'),
							]"
							placeholder="Melhor e-mail"
							autocomplete="email"
							@input="isEmailValid"
						/>
						<span class="form-text text-muted">
							<small>
								Este e-mail será utilizado para acessar a
								aplicação
							</small>
						</span>
					</div>
				</div>

				<div class="form-row">
					<div class="form-group col-12 required">
						<label for="password">Senha</label>
						<input
							id="password"
							v-model="form.password"
							type="password"
							name="password"
							class="form-control"
							placeholder="*********"
							autocomplete="new-password"
						/>
					</div>
				</div>

				<div class="form-row">
					<div class="form-group col-12 required">
						<label for="password_confirm">
							Confirmação de Senha
						</label>
						<input
							id="password_confirm"
							v-model="form.password_confirm"
							type="password"
							name="password_confirm"
							class="form-control"
							placeholder="*********"
							autocomplete="new-password"
						/>
					</div>
				</div>

				<div class="form-row">
					<div class="form-group col-12 required">
						<div class="form-check">
							<input
								id="agree_terms"
								v-model="form.agree_terms"
								type="checkbox"
								class="form-check-input"
							/>
							<label
								class="form-check-label text-muted"
								for="agree_terms"
							>
								Eu concordo com os
								<a
									:href="vueRoute('guest.privacy.term')"
									target="_blank"
								>
									Termos de Uso e Declaração de Privacidade
								</a>
							</label>
						</div>
					</div>
				</div>

				<div v-cloak class="pt-2 pb-3 float-right">
					<button
						v-if="!processSubmitted"
						type="submit"
						class="btn btn-blue btn-rounded waves-effect waves-light"
					>
						Confirmar
					</button>
					<button
						v-else
						class="btn btn-info btn-rounded"
						type="button"
						disabled
					>
						<span
							class="spinner-border spinner-border-sm mr-1"
							role="status"
							aria-hidden="true"
						/>
						Processando...
					</button>
				</div>
			</form>
		</div>
	</div>
</template>

<script>
import Cookies from 'js-cookie'

export default {
	name: 'Sponsor',
	data() {
		return {
			imageUrl: window.imageUrlP,
			logoUrl: window.logoP,
			company: window.company,
			people: window.people,
			showSponsorField:
				window.showSponsorField !== undefined
					? window.showSponsorField
					: true,
			showLegalName:
				window.showLegalName !== undefined
					? window.showLegalName
					: false,
			form: {
				person_type: 'people',
				userable: {},
				phone: null,
				username: null,
				sponsor: null,
				password: null,
				password_confirm: null,
				agree_terms: null,
			},
			usernameField: window.usernameField,
			usernameValidation: {
				checking: false,
				available: null,
				message: '',
			},
		}
	},
	computed: {
		useSeparateName() {
			return window.useSeparateName || false
		},
		usernameHelpText() {
			return window.usernameHelpText || ''
		},
	},
	watch: {
		'form.person_type': {
			deep: false,
			handler(newValue) {
				if (newValue === 'people') {
					this.form.userable = this.defaultPeople()
				} else {
					this.form.userable = this.defaultCompany()
				}
			},
		},
	},
	mounted() {
		this.validateSponsorInCookie()
		this.checkTypePermssionRegister()
	},
	methods: {
		/**
		 * Sanitiza o username removendo caracteres especiais
		 * Permite apenas letras, números, hífen e underscore
		 */
		sanitizeUsername(event) {
			const value = event.target.value
			// Remove todos os caracteres que não sejam letras, números, hífen ou underscore
			const sanitized = value.replace(/[^a-zA-Z0-9_-]/g, '')
			this.form.username = sanitized

			// Reset validation when user types
			if (this.usernameValidation.message) {
				this.usernameValidation = {
					checking: false,
					available: null,
					message: '',
				}
			}
		},

		/**
		 * Verifica se o username está disponível
		 */
		async checkUsernameAvailability() {
			// Não validar se username estiver vazio
			if (!this.form.username || this.form.username.trim() === '') {
				this.usernameValidation = {
					checking: false,
					available: null,
					message: '',
				}
				return
			}

			// Validar formato antes de fazer requisição
			if (!/^[a-zA-Z0-9_-]+$/.test(this.form.username)) {
				this.usernameValidation = {
					checking: false,
					available: false,
					message: `${this.usernameField.label} deve conter apenas letras, números, hífen e underscore`,
				}
				return
			}

			// Inicia verificação
			this.usernameValidation.checking = true
			this.usernameValidation.message = ''

			try {
				const response = await this.$axios.post(
					route('api.username.check'),
					{
						username: this.form.username,
					},
				)

				this.usernameValidation = {
					checking: false,
					available: response.data.available,
					message: response.data.message,
				}
			} catch (error) {
				if (error.response && error.response.data) {
					this.usernameValidation = {
						checking: false,
						available: false,
						message:
							error.response.data.message ||
							`Erro ao verificar ${this.usernameField.label}`,
					}
				} else {
					this.usernameValidation = {
						checking: false,
						available: false,
						message: `Erro ao verificar ${this.usernameField.label}`,
					}
				}
			}
		},

		/**
		 * TODO: Validar a regra do cookie
		 *
		 * Hoje esta inserindo o cookie quando acessa pelo link do patrocinador
		 * Caso acessar com o link de outro patrocinador, o cookie será alterado
		 * Uma alternativa será salvar no servidor, pegando o ip do utilizador para que evite que mude pelo browser
		 */
		validateSponsorInCookie() {
			let cookie = Cookies.get('sponsor')

			if (cookie && !window.sponsorP) {
				this.form.sponsor = cookie
			} else if (window.sponsorP) {
				this.form.sponsor = window.sponsorP
				Cookies.set('sponsor', this.form.sponsor)
			}

			// Apenas desabilita o campo se ele estiver visível
			if (this.form.sponsor && this.showSponsorField) {
				const sponsorField = document.getElementById('sponsor')
				if (sponsorField) {
					sponsorField.setAttribute('disabled', 'disabled')
				}
			}
		},
		defaultPeople() {
			return {
				tax_number: null,
				rg: null,
				cpf: null,
				full_name: null,
				first_name: null,
				last_name: null,
				additional: null,
				birth_date: null,
			}
		},

		defaultCompany() {
			return {
				legal_name: '',
				trade_name: '',
				tax_number: '',
				ie: '',
				people_contact: '',
			}
		},

		validateForm() {
			let errors = {}

			// Validar patrocinador apenas se o campo estiver visível
			if (this.showSponsorField && !this.form.sponsor) {
				errors['sponsor'] = 'Patrocinador é um campo obrigatório'
			}

			if (!this.form.username) {
				errors['username'] =
					`${this.usernameField.label} é um campo obrigatório`
			} else if (!/^[a-zA-Z0-9_-]+$/.test(this.form.username)) {
				errors['username'] =
					`${this.usernameField.label} deve conter apenas letras, números, hífen e underscore`
			} else if (this.usernameValidation.available === false) {
				errors['username'] = this.usernameValidation.message
			}

			if (this.form.person_type === 'people') {
				if (this.useSeparateName) {
					if (!this.form.userable.first_name) {
						errors['firstName'] = 'Nome é um campo obrigatório'
					}

					if (!this.form.userable.last_name) {
						errors['lastName'] = 'Sobrenome é um campo obrigatório'
					}
				} else {
					if (
						!this.form.userable.full_name ||
						HelperJS.isEmptyString(this.form.userable.full_name)
					) {
						errors['fullName'] =
							'Nome Completo é um campo obrigatório'
					} else {
						let name = this.form.userable.full_name.split(' ')
						this.form.userable.first_name = name.shift()
						this.form.userable.last_name = name.join(' ')
					}
				}
			}

			if (!this.form.phone) {
				errors['phone'] = 'Telefone para contato é um campo obrigatório'
			}

			if (!this.form.email) {
				errors['email'] = 'Digite um e-mail válido'
			}

			if (!this.form.password) {
				errors['password'] = 'Digite uma senha válida'
			}

			if (this.form.password !== this.form.password_confirm) {
				errors['password_confirm'] =
					'A confirmação de senha não corresponde com a senha indicada'
			}

			if (!this.form.agree_terms) {
				errors['agree_terms'] =
					'É necessário aceitar os Termos de Uso e Declaração de Privacidade'
			}

			if (Object.keys(errors).length > 0) {
				this.$refs['groupedErrors'].set(errors)
				return false
			}

			return true
		},

		checkTypePermssionRegister() {
			if (this.company && !this.people) {
				this.form.person_type = 'company'
			}

			if (this.people && !this.company) {
				this.form.person_type = 'people'
			}

			if (!this.people && !this.company) {
				this.people = 1
				this.company = 1
				this.form.person_type = 'people'
			}
		},

		store() {
			console.debug(this.form)

			if (!this.validateForm()) {
				return false
			}

			this.interceptorRequest(
				'post',
				route('api.sponsor.storeGuest'),
				this.form,
				'api',
				'Cadastro confirmado com sucesso!',
			)
		},
	},
}
</script>

<style lang="scss">
[v-cloak] {
	display: none !important;
	visibility: hidden !important;
}

[v-cloak]::before {
	content: '';
	display: block;
	width: 16px;
	height: 16px;
}

.auth-fluid .auth-fluid-form-box {
	max-width: 430px;
}

.form-control {
	height: calc(2.1em + 0.56rem + 2px);
	padding: 0.28rem 0.8rem;
	font-size: 0.7875rem;
	line-height: 1.5;
	border-radius: 0.2rem;
}

.form-group.required label:not(:empty)::after {
	content: ' *';
	color: #ff0000;
}

.auth-fluid {
	.auth-fluid-form-box {
		padding: 1rem 2rem;
	}
}

.alert {
	padding: 1px;

	h4 {
		margin-left: 12px;
	}
}

//.img-indication {
//  width: 100%;
//  height: auto;
//}
//
//.auth-fluid {
//  background: white;
//
//  .auth-fluid-right {
//    padding: 0 !important;
//    flex: 1;
//    position: relative;
//    background-color: white;
//    align-self: center;
//  }
//}
</style>
